import React from 'react';
import type { FrequencyEnum } from 'venn-api';
import { getAppTitle, Icon, Tooltip } from 'venn-ui-kit';
import { Numbers } from 'venn-utils';
import StatefulNumericInput from '../../../stateful-numeric-input/StatefulNumericInput';
import { capitalize } from 'lodash';
import { cn } from '@/lib/utils';

interface LagInputProps {
  frequency: FrequencyEnum;
  numLags: number;
  suggestedNumLags?: number;
  onLagUpdate: (value: number) => void;
  inputClassName?: string;
  trailingElement?: React.ReactNode;
  variant?: 'stacked' | 'inline';
  testId?: string;
}

const formatter = (value: string | number) => {
  return Numbers.formatNumber(value, 0);
};

const LagInput = ({
  frequency,
  numLags: rawNumLags,
  suggestedNumLags,
  onLagUpdate,
  inputClassName,
  trailingElement,
  variant,
  testId,
}: LagInputProps) => {
  const onValueChange = (val: number) => {
    onLagUpdate(Math.round(val));
  };

  const maxNumLags = frequency === 'QUARTERLY' ? 6 : 12;

  const numLags = rawNumLags > 0 ? rawNumLags : (suggestedNumLags ?? 0);

  const lagTooltip =
    suggestedNumLags && suggestedNumLags !== numLags
      ? `Default lag recommendation (${suggestedNumLags}) is based on ${getAppTitle()} analysis.`
      : `Default lag recommendation is based on ${getAppTitle()} analysis.`;

  return (
    <div className="font-bold text-[14px] flex">
      <div className={cn('flex flex-col', variant === 'stacked' && 'gap-[6px]', variant === 'inline' && 'gap-[3px]')}>
        <div data-testid="lag-header" className="flex items-center leading-[24px] pr-[3px]">
          {capitalize(frequency)} Lags
          <Tooltip usePortal content={lagTooltip}>
            <Icon className="ml-1 text-venn-grey-80" type="circle-info" prefix="far" />
          </Tooltip>
        </div>
        <StatefulNumericInput
          min={1}
          max={maxNumLags}
          precision={0}
          value={`${numLags === 0 ? '' : numLags}`}
          formatter={formatter}
          onValueChange={onValueChange}
          className={cn('w-full', inputClassName)}
          data-testid={testId}
        />
      </div>
      {trailingElement && <div className="self-end">{trailingElement}</div>}
    </div>
  );
};

export default LagInput;
